import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Briefcase, UserSearch, Handshake, Search, BadgeCheck, Sparkles, Building2,
  MessagesSquare, Clock, TrendingUp, ShieldCheck, Send, ChevronRight,
} from "lucide-react";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const HERO_IMG = "https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwY29ycG9yYXRlJTIwcHJvZmVzc2lvbmFsc3xlbnwwfHx8fDE3ODQwNDk4NDB8MA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG = "https://images.unsplash.com/photo-1573165706511-3ffde6ef1fe3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxkaXZlcnNlJTIwY29ycG9yYXRlJTIwcHJvZmVzc2lvbmFsc3xlbnwwfHx8fDE3ODQwNDk4NDB8MA&ixlib=rb-4.1.0&q=85";
const WAREHOUSE_IMG = "https://images.pexels.com/photos/4484152/pexels-photo-4484152.jpeg";
const HOSPITALITY_IMG = "https://images.unsplash.com/photo-1779591211498-70335c96bb69?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxob3NwaXRhbGl0eSUyMHN0YWZmfGVufDB8fHx8MTc4NDA0OTg0MHww&ixlib=rb-4.1.0&q=85";

const INDUSTRIES = [
  { name: "Warehousing & Logistics", img: WAREHOUSE_IMG },
  { name: "Hospitality", img: HOSPITALITY_IMG },
  { name: "Customer Service" },
  { name: "Manufacturing" },
  { name: "Retail" },
  { name: "Theme Parks & Entertainment" },
  { name: "Administrative & Office Support" },
  { name: "Human Resources" },
  { name: "Skilled Trades" },
  { name: "Environmental & Conservation" },
];

const FAQS = [
  { q: "How much does Recruvo cost job seekers?", a: "Recruvo is 100% free for job seekers. We're paid by the employer clients that hire the people we place." },
  { q: "How are candidates screened?", a: "Every candidate goes through resume review, a live recruiter screening call, skills validation, work-authorization confirmation, and — when required — background checks." },
  { q: "What industries do you specialize in?", a: "We staff HR, Administrative, Customer Service, Hospitality, Theme Parks, Manufacturing, Warehousing, Retail, Skilled Trades, and Environmental & Conservation roles." },
  { q: "How fast can you fill an open role?", a: "For high-volume industries we typically submit qualified candidates in 48-72 hours. Specialized roles may take 5-10 business days." },
  { q: "Do you offer contract or temp-to-hire?", a: "Yes — direct-hire, contract, temp-to-hire, and seasonal placements are all supported." },
];

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", company: "", message: "", kind: "contact" });
  const [industries, setIndustries] = useState(INDUSTRIES);

  useEffect(() => {
    api.get("/jobs", { params: { is_featured: true, limit: 6 } }).then(r => setFeatured(r.data.items || [])).catch(() => {});
  }, []);

  const submitContact = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", contact);
      toast.success("Thanks — we'll be in touch shortly.");
      setContact({ name: "", email: "", phone: "", company: "", message: "", kind: "contact" });
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative bg-navy text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Diverse professionals" className="w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative container-ax py-20 md:py-32 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="eyebrow mb-4 text-gold">Connecting Great People with Great Companies.</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
              The Right Talent. <br/>
              <span className="text-gold">The Right Opportunity.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-200/90 leading-relaxed">
              Connecting great people with great companies through technology, expertise, and personal relationships.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/register?role=candidate" className="btn-gold text-base" data-testid={TID.heroLookingForWork}>
                <UserSearch className="w-5 h-5" /> I'm Looking for Work
              </Link>
              <Link to="/register?role=employer" className="btn-navy border border-white/20 text-base bg-white text-navy hover:bg-slate-100"
                data-testid={TID.heroLookingToHire} style={{ background: "white", color: "#0F172A" }}>
                <Handshake className="w-5 h-5" /> I'm Looking to Hire
              </Link>
            </div>

            {/* Trust bar */}
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-300/90">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-gold" /> Vetted candidates</div>
              <div className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-gold" /> 90-day placement guarantee</div>
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-gold" /> 48-72 hr submissions</div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section">
        <div className="container-ax">
          <div className="max-w-2xl">
            <div className="eyebrow mb-2">How It Works</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy">A clear, human process — no black box.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { icon: Search, title: "Discover", body: "Employers post roles and job seekers create rich profiles. Our recruiters actively hunt for the right match." },
              { icon: MessagesSquare, title: "Screen", body: "Every candidate is interviewed, verified for work authorization, and evaluated against job requirements." },
              { icon: Handshake, title: "Place", body: "We submit shortlists to employers, coordinate interviews, and support both sides through hire day and beyond." },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="card-lift bg-white border border-slate-200 rounded-xl p-6">
                  <div className="h-11 w-11 rounded-lg bg-gold/10 text-gold grid place-items-center mb-4">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="text-xs tracking-widest uppercase text-slate-500">Step {i+1}</div>
                  <h3 className="font-display text-xl font-semibold text-navy mt-1">{s.title}</h3>
                  <p className="text-slate-600 mt-2 text-sm leading-relaxed">{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED JOBS */}
      <section className="section bg-slate-50">
        <div className="container-ax">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="eyebrow mb-2">Featured Jobs</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-navy">Openings actively hiring right now.</h2>
            </div>
            <Link to="/jobs" className="text-navy font-medium hover:text-gold transition-colors inline-flex items-center gap-1">
              Browse all jobs <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div data-testid={TID.homeFeaturedJobsGrid} className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.length === 0 && (
              <div className="col-span-full text-center text-slate-500 py-10">Positions being loaded…</div>
            )}
            {featured.map(j => (
              <Link key={j.id} to={`/jobs/${j.id}`} data-testid={TID.jobCard(j.id)}
                className="card-lift bg-white border border-slate-200 rounded-xl p-5 block">
                <div className="text-xs uppercase tracking-widest text-slate-500">{j.industry || "Career"}</div>
                <h3 className="font-display text-lg font-semibold text-navy mt-1 line-clamp-2">{j.title}</h3>
                <div className="text-sm text-slate-600 mt-1">{j.employer_name}</div>
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  {j.employment_type && <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">{j.employment_type}</span>}
                  {j.work_arrangement && <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">{j.work_arrangement}</span>}
                  {j.city && <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">{j.city}{j.state ? `, ${j.state}` : ""}</span>}
                </div>
                <div className="mt-4 text-sm font-medium text-navy inline-flex items-center gap-1">
                  View role <ChevronRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section id="industries" className="section">
        <div className="container-ax">
          <div className="max-w-2xl mb-10">
            <div className="eyebrow mb-2">Industries We Serve</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy">Deep expertise across ten specialty verticals.</h2>
          </div>
          <div data-testid={TID.homeIndustriesGrid} className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {industries.map((ind, i) => (
              <div key={i} className="card-lift bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="aspect-[4/3] bg-slate-100 relative">
                  {ind.img ? (
                    <img src={ind.img} alt={ind.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-navy/20 font-display font-bold text-3xl">
                      {ind.name.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="p-3 text-sm font-medium text-navy">{ind.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="section bg-navy text-white">
        <div className="container-ax">
          <div className="grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-5">
              <div className="eyebrow mb-2 text-gold">Why Choose Recruvo</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold">A staffing partner that actually shows up.</h2>
              <p className="mt-4 text-slate-300 max-w-md leading-relaxed">
                We built Recruvo for the industries the big platforms overlook — the frontline of hospitality, warehousing, entertainment, and skilled trades. Every placement is human-reviewed.
              </p>
            </div>
            <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
              {[
                { icon: BadgeCheck, title: "Every candidate screened", body: "Live phone or video conversation before we submit anyone to you." },
                { icon: TrendingUp, title: "90-day guarantee", body: "If a placement doesn't work out, we replace at no extra fee." },
                { icon: Sparkles, title: "AI-assisted matching", body: "Our recruiters use AI to find matches faster — but a human always decides." },
                { icon: Building2, title: "Employer-first", body: "We only submit qualified candidates. No résumé spam." },
              ].map((f, i) => {
                const Icon = f.icon;
                return (
                  <div key={i} className="rounded-xl border border-white/10 bg-white/5 p-5">
                    <Icon className="w-5 h-5 text-gold" />
                    <h3 className="font-display text-lg font-semibold mt-3">{f.title}</h3>
                    <p className="text-sm text-slate-300 mt-1">{f.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section">
        <div className="container-ax grid md:grid-cols-2 gap-10">
          <div className="card-lift border border-slate-200 rounded-xl p-8 bg-white">
            <div className="eyebrow mb-3">Employer Testimonial</div>
            <blockquote className="font-display text-xl text-navy leading-snug">
              "Recruvo filled twelve warehouse roles in three weeks. Every candidate showed up on day one — that's unheard of."
            </blockquote>
            <div className="mt-5 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-navy text-white grid place-items-center font-display">PM</div>
              <div>
                <div className="text-sm font-semibold text-navy">Priya Menon</div>
                <div className="text-xs text-slate-500">Talent Director, Nova Retail Group</div>
              </div>
            </div>
          </div>
          <div className="card-lift border border-slate-200 rounded-xl p-8 bg-white">
            <div className="eyebrow mb-3">Job-Seeker Testimonial</div>
            <blockquote className="font-display text-xl text-navy leading-snug">
              "My recruiter checked in daily and prepped me for every interview. I'm two months in and loving the new role."
            </blockquote>
            <div className="mt-5 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-gold text-navy grid place-items-center font-display">JR</div>
              <div>
                <div className="text-sm font-semibold text-navy">Jordan R.</div>
                <div className="text-xs text-slate-500">Placed as Warehouse Team Lead</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="section bg-slate-50">
        <div className="container-ax grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <div className="eyebrow mb-2">About the Agency</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy">A recruiting agency built by people who've done the work.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Recruvo Group is a modern staffing partner focused on hourly, skilled, and hard-to-fill roles. We combine deep industry knowledge with modern tooling to move faster than traditional agencies — without cutting corners on quality.
            </p>
            <div className="mt-6 grid grid-cols-3 gap-4">
              <div><div className="font-display text-3xl font-bold text-navy">10+</div><div className="text-xs text-slate-500">Industries served</div></div>
              <div><div className="font-display text-3xl font-bold text-navy">72h</div><div className="text-xs text-slate-500">Avg. submission</div></div>
              <div><div className="font-display text-3xl font-bold text-navy">90d</div><div className="text-xs text-slate-500">Placement guarantee</div></div>
            </div>
          </div>
          <div className="md:col-span-6 order-1 md:order-2">
            <img src={ABOUT_IMG} alt="Consulting team" className="rounded-2xl w-full h-80 object-cover shadow-lg" />
          </div>
        </div>
      </section>

      {/* CONTACT / CONSULTATION */}
      <section id="contact" className="section">
        <div className="container-ax grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="eyebrow mb-2">Contact / Schedule a Consultation</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy">Let's talk about your hiring goals.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Whether you're a company that needs to fill 2 or 200 roles, or a job seeker looking for your next step — a real person will respond within one business day.
            </p>
            <div className="mt-6 space-y-2 text-sm text-slate-600">
              <div><strong className="text-navy">Email:</strong> hello@recruvogroup.com</div>
              <div><strong className="text-navy">Phone:</strong> (407) 555-0100</div>
            </div>
          </div>
          <form onSubmit={submitContact} className="md:col-span-7 rounded-2xl border border-slate-200 bg-white p-6 md:p-8 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-600">Name</label>
                <input required value={contact.name} onChange={e => setContact({...contact, name: e.target.value})}
                  data-testid={TID.homeContactName}
                  className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:border-gold" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Email</label>
                <input required type="email" value={contact.email} onChange={e => setContact({...contact, email: e.target.value})}
                  data-testid={TID.homeContactEmail}
                  className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:border-gold" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Company (optional)</label>
                <input value={contact.company} onChange={e => setContact({...contact, company: e.target.value})}
                  className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Phone (optional)</label>
                <input value={contact.phone} onChange={e => setContact({...contact, phone: e.target.value})}
                  className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">How can we help?</label>
              <textarea required rows={5} value={contact.message} onChange={e => setContact({...contact, message: e.target.value})}
                data-testid={TID.homeContactMessage}
                className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="text-xs text-slate-600 flex items-center gap-2">
                <input type="radio" name="kind" value="contact" checked={contact.kind === "contact"} onChange={e => setContact({...contact, kind: e.target.value})} />
                General inquiry
              </label>
              <label className="text-xs text-slate-600 flex items-center gap-2">
                <input type="radio" name="kind" value="consultation" checked={contact.kind === "consultation"} onChange={e => setContact({...contact, kind: e.target.value})} />
                Schedule consultation
              </label>
            </div>
            <button type="submit" data-testid={TID.homeContactSubmit} className="btn-navy">
              <Send className="w-4 h-4" /> Send message
            </button>
          </form>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="section bg-slate-50">
        <div className="container-ax max-w-3xl">
          <div className="eyebrow mb-2">Frequently Asked Questions</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-navy">Everything you'd want to know.</h2>
          <div className="mt-8" data-testid={TID.homeFaq}>
            <Accordion type="single" collapsible>
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`}>
                  <AccordionTrigger className="text-left font-display text-lg text-navy hover:text-gold">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
}
